import {
  Autocomplete,
  CircularProgress,
  Modal,
  TextField,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomButton from "../Buttons/Button";
import { getAllUser } from "../../store/actions/userManagement/management";
import { searchUsers } from "../../store/actions/userManagement/searchUsers";
import { sellOrderForUser } from "../../store/actions/stocks/sellOrderForUser";
import { getCurrencySymbol } from "../../Helper/getCurrencySymbol";

const buildUserOption = (user) => ({
  ...user,
  label: `${user?.name || "Unnamed"} — ${user?.email || ""} (ID: ${user?.id})`,
});

const orderTypeOptions = [
  { value: "market", label: "Market" },
  { value: "limit", label: "Limit" },
];

const purificationMethodOptions = [
  { value: "admin_donate", label: "Admin Donate" },
  { value: "self_purify", label: "User Donate" },
];

// Accepts either a `stock` (sell from the stock's detail page — admin picks the
// user) or an `order` (sell an approved buy back — the buy order already carries
// the user, stock and volume, so those are pre-filled and locked).
function SellStockForUserModal({ open, onClose, stock, order }) {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.stocks?.sellForUserPending);
  const allUsers = useSelector((state) => state?.user?.allUsers) || [];
  const allUsersPending = useSelector((state) => state?.user?.allUsersPending);

  const resolvedStock = order
    ? { ...order.stock, currency: order.currency }
    : stock;
  const presetUser = order?.user_id
    ? {
        id: order.user_id,
        name: order.user_name,
        email: order.user_email,
      }
    : null;
  const userLocked = Boolean(presetUser);

  const [selectedUser, setSelectedUser] = useState(null);
  const [orderType, setOrderType] = useState("market");
  const [volume, setVolume] = useState("");
  const [limitPrice, setLimitPrice] = useState("");
  const [purificationMethod, setPurificationMethod] = useState("admin_donate");
  const [inputValue, setInputValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const searchTimer = useRef(null);

  const currencySymbol = getCurrencySymbol(resolvedStock?.currency);

  useEffect(() => {
    if (open) {
      setSelectedUser(presetUser);
      setOrderType("market");
      setVolume(order?.volume != null ? String(order.volume) : "");
      setLimitPrice("");
      setPurificationMethod("admin_donate");
      setInputValue("");
      setSearchResults([]);
      if (!userLocked && (!allUsers || allUsers.length < 1)) {
        dispatch(getAllUser());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced server-side search so admins can find users not in the
  // initially loaded list.
  const handleInputChange = (_, value, reason) => {
    setInputValue(value);
    if (reason === "reset") return;

    if (searchTimer.current) clearTimeout(searchTimer.current);

    const term = value?.trim();
    if (!term || term.length < 2) {
      setSearching(false);
      setSearchResults([]);
      return;
    }

    setSearching(true);
    searchTimer.current = setTimeout(() => {
      dispatch(searchUsers(term))
        .unwrap()
        .then((users) => setSearchResults(Array.isArray(users) ? users : []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  // With an active search term show the server results; otherwise fall back to
  // the initially loaded users. Always keep the selected user in the list so
  // MUI can resolve the current value. De-duplicate by id.
  const hasSearchTerm = inputValue?.trim()?.length >= 2;
  const baseUsers = hasSearchTerm ? searchResults : allUsers || [];
  const mergedUsers = [
    ...(selectedUser ? [selectedUser] : []),
    ...baseUsers,
  ].reduce((acc, user) => {
    if (user?.id != null && !acc.some((u) => u?.id === user.id)) {
      acc.push(user);
    }
    return acc;
  }, []);

  const userOptions = mergedUsers.map(buildUserOption);

  const handleSubmit = () => {
    if (!selectedUser?.id) {
      toast.error("Please select a user to sell for.");
      return;
    }
    if (!resolvedStock?.id) {
      toast.error("This order has no stock to sell.");
      return;
    }
    if (!volume || Number(volume) <= 0) {
      toast.error("Please enter a valid volume.");
      return;
    }
    if (orderType === "limit" && (!limitPrice || Number(limitPrice) <= 0)) {
      toast.error("Please enter a valid limit price.");
      return;
    }

    const data = {
      user_id: Number(selectedUser.id),
      stock_id: Number(resolvedStock.id),
      order_type: orderType,
      volume: Number(volume),
      purification_method: purificationMethod,
      ...(orderType === "limit" && { limit_price: Number(limitPrice) }),
    };

    dispatch(
      sellOrderForUser({
        data,
        callback: () => onClose(),
      }),
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          maxWidth: "92%",
          maxHeight: "85%",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 2,
        }}
      >
        <IconButton
          aria-label="close"
          sx={{ position: "absolute", top: 19, right: 10 }}
          onClick={onClose}
        >
          <ClearIcon />
        </IconButton>

        <div className="w-full p-3">
          <h1 className="font-semibold text-2xl mb-1">Sell for a User</h1>
          <p className="text-sm mb-6 text-[#475467]">
            Place a {resolvedStock?.symbol ? `${resolvedStock.symbol} ` : ""}
            sell order on behalf of a user.
          </p>

          <label className="text-sm font-[600] text-[#344054]">
            {userLocked ? "User" : "Select User"}
          </label>
          {userLocked ? (
            <div className="w-full mb-4 mt-1 border border-[#D0D5DD] rounded-lg p-3 bg-[#F9FAFB]">
              <p className="font-[600] text-sm">
                {selectedUser?.name || "Unnamed"}
              </p>
              <p className="text-xs text-[#475467]">
                {selectedUser?.email || ""} (ID: {selectedUser?.id})
              </p>
            </div>
          ) : (
            <div className="w-full mb-4 mt-1">
              <Stack spacing={2} sx={{ width: "100%" }}>
                <Autocomplete
                  options={userOptions}
                  value={selectedUser}
                  inputValue={inputValue}
                  loading={allUsersPending || searching}
                  filterOptions={(options) => options}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  onChange={(_, newValue) => setSelectedUser(newValue)}
                  onInputChange={handleInputChange}
                  noOptionsText={
                    inputValue?.trim()?.length >= 2
                      ? "No users found"
                      : "Type at least 2 characters to search"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search user by name, email or ID"
                      sx={{
                        "& .MuiInputBase-input": {
                          height: "20px",
                          padding: "15px",
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searching ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
            </div>
          )}

          {resolvedStock?.symbol && (
            <>
              <label className="text-sm font-[600] text-[#344054]">Stock</label>
              <div className="w-full mb-4 mt-1 border border-[#D0D5DD] rounded-lg p-3 bg-[#F9FAFB]">
                <p className="font-[600] text-sm">
                  {resolvedStock.symbol}
                  {resolvedStock?.name ? ` — ${resolvedStock.name}` : ""}
                </p>
                {resolvedStock?.current_price != null && (
                  <p className="text-xs text-[#475467]">
                    Current Price: {currencySymbol}{" "}
                    {Number(resolvedStock.current_price).toLocaleString()}
                  </p>
                )}
              </div>
            </>
          )}

          <label className="text-sm font-[600] text-[#344054]">
            Order Type
          </label>
          <select
            value={orderType}
            onChange={(e) => setOrderType(e.target.value)}
            className="w-full border border-[#D0D5DD] rounded-lg p-3 mt-1 mb-4 outline-none"
          >
            {orderTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <label className="text-sm font-[600] text-[#344054]">Volume</label>
          <input
            type="number"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
            placeholder="Enter number of shares"
            className="w-full border border-[#D0D5DD] rounded-lg p-3 mt-1 mb-4 outline-none"
          />

          {orderType === "limit" && (
            <>
              <label className="text-sm font-[600] text-[#344054]">
                Limit Price ({currencySymbol})
              </label>
              <input
                type="number"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                placeholder="Enter limit price"
                className="w-full border border-[#D0D5DD] rounded-lg p-3 mt-1 mb-4 outline-none"
              />
            </>
          )}

          <label className="text-sm font-[600] text-[#344054]">
            Purification Method
          </label>
          <select
            value={purificationMethod}
            onChange={(e) => setPurificationMethod(e.target.value)}
            className="w-full border border-[#D0D5DD] rounded-lg p-3 mt-1 mb-4 outline-none"
          >
            {purificationMethodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="flex mt-2 w-full items-center">
            <CustomButton
              variant="outlined"
              customColor="#fff"
              children="Cancel"
              width="100%"
              height="44px"
              borderColor="#D0D5DD"
              color="#344054"
              margin="0px 25px 0px 0px"
              onClick={onClose}
            />
            <CustomButton
              variant="contained"
              customColor="#B42318"
              children="Place Sell Order"
              width="100%"
              height="44px"
              loading={loading}
              padding="10px"
              color="#fff"
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default SellStockForUserModal;
